import { loadStripe } from '@stripe/stripe-js';
import { getCountryCode, getCountryFields } from './countryUtils';

// PRODUCTION: Test keys commented out - must use environment variables
// const DEFAULT_TEST_PUBLISHABLE_KEY = 'pk_test_51OfcsAK5LRvvauRY1Kqp4ssvkKlN6PIBtMtI7bdSWfcb5577W1hnWUezwSMIdzIgG3g8vWMj8yGzjHgBVc4kkxNB000iDKwzyF'; // COMMENTED FOR PRODUCTION
// const DEFAULT_TEST_CLIENT_SECRET = 'sk_test_51OfcsAK5LRvvauRY8B0k3F1KuYq56mAoai1O1pprO42kEPPkABLNRNyPe7ldTbRh7jwBWBSAfyJFqeVlhpBsXQG200aenGKv40'; // COMMENTED FOR PRODUCTION

const isPlaceholderKey = (value) => {
  if (!value) {
    return true;
  }

  const placeholders = [
    'your_stripe_publishable_key_here',
    'pk_test_your_stripe_publishable_key_here',
    'pk_test_your_key_here'
  ];

  return placeholders.includes(value);
};

const isValidPublishableKey = (value) => {
  return typeof value === 'string' && (value.startsWith('pk_test_') || value.startsWith('pk_live_'));
};

// Safe function to get Stripe publishable key
export const getStripeKey = () => {
  const keyFromEnv = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  
  // PRODUCTION: Require environment variable - no test key fallback
 //  const key = !isPlaceholderKey(keyFromEnv) ? keyFromEnv : DEFAULT_TEST_PUBLISHABLE_KEY; // COMMENTED FOR PRODUCTION
  const key = !isPlaceholderKey(keyFromEnv) ? keyFromEnv : null; // PRODUCTION: No fallback to test key

  // DEBUG: Log which key is being used
  if (key) {
    const keyType = key.startsWith('pk_live_') ? 'LIVE' : 
                    key.startsWith('pk_test_') ? 'TEST' : 'UNKNOWN';
    console.log(`🔍 [FRONTEND STRIPE DEBUG] Using ${keyType} mode - Key prefix: ${key.substring(0, 7)}...`);
  }

  if (!key || !isValidPublishableKey(key)) {
    console.warn('Stripe publishable key not configured. Payment features will be disabled.');
    return null;
  }

  // PRODUCTION: Removed test key detection
  // if (key === DEFAULT_TEST_PUBLISHABLE_KEY) {
  //   console.info('Using default Stripe test publishable key for local testing.');
  // }

  return key;
};

export const getStripeClientSecret = () => {
  // PRODUCTION: Require environment variable - no test key fallback
//   const secret = process.env.REACT_APP_STRIPE_CLIENT_SECRET || DEFAULT_TEST_CLIENT_SECRET; // COMMENTED FOR PRODUCTION
  const secret = process.env.REACT_APP_STRIPE_CLIENT_SECRET; // PRODUCTION: No fallback to test key

  if (!secret || (!secret.startsWith('sk_test_') && !secret.startsWith('sk_live_'))) {
    console.warn('Stripe client secret not configured or invalid format.');
    return undefined;
  }

  // PRODUCTION: Removed test key detection
  // if (secret === DEFAULT_TEST_CLIENT_SECRET) {
  //   console.info('Using default Stripe test client secret for local testing.');
  // }

  return secret;
};

// Safe Stripe initialization
export const initStripe = async () => {
  try {
    const key = getStripeKey();
    if (!key) {
      console.warn('Stripe initialization skipped - no valid publishable key');
      return null;
    }

    const stripe = await loadStripe(key);
    if (!stripe) {
      console.error('Failed to load Stripe');
      return null;
    }

    console.log('Stripe initialized successfully');
    return stripe;
  } catch (error) {
    console.error('Error initializing Stripe:', error);
    return null;
  }
};

// Create Stripe promise for use with Elements
export const createStripePromise = () => {
  const key = getStripeKey();
  return key ? loadStripe(key) : null;
};

// Check if Stripe is available
export const isStripeAvailable = () => {
  return getStripeKey() !== null;
};

// Get Stripe configuration
export const getStripeConfig = () => {
  const countryCode = getCountryCode();
  const countryFields = getCountryFields(countryCode);
  const currency = countryFields.currency || 'AUD';
  return {
    publishableKey: getStripeKey(),
    clientSecret: getStripeClientSecret(),
    currency: (process.env.REACT_APP_STRIPE_CURRENCY || currency).toLowerCase(),
    isAvailable: isStripeAvailable()
  };
};
